/**
 * L2 drill-down: the API gateway in full — every result code, every failing
 * outbound call, and the whole ranked operation list.
 *
 * **This page is API Management's data, not F&O's.** The queries behind it name
 * no application: they read every row in `AppRequests` / `AppDependencies`, and
 * measured 22 Aug 2026 every one of those rows came from the `NO-APIM-NAX West
 * Europe` gateway in front of F&O. The page used to be titled "F&O request
 * health", which promised the ERP's own health and delivered the gateway's
 * traffic. See `./requests-section.tsx` for the full note, including the payload
 * change that would let this be computed rather than dated.
 *
 * The `Mean app`, `Mean SQL` and `SQL calls` columns are gone from the ranking
 * grid (22 Aug 2026). No `SQL`-type dependency row exists in the workspace, so
 * `avg_sql_ms` is a coalesced zero and `avg_app_ms` is just the mean duration
 * again — three columns that looked like a decomposition of request time and
 * were an artefact of a join that never matched. The fields stay in the payload;
 * only the rendering is gone.
 *
 * The board's result-code donut drills in here carrying `?code=` (the folded
 * "Other codes" segment carries every code it folded), and the `FilterRow`
 * above the result-code grid reads the same param — so the drill is shareable,
 * the back button undoes it, and a refresh keeps the view (§4.2).
 *
 * As on the forms page: no search box, because there is nothing personal to
 * search for. Outbound calls carry `dependency_type` and `target` only; the
 * report's 200-character slice of SQL statement text has no field here, because
 * a dependency name embeds the literal data values it ran against.
 */

import {
  Alert,
  Badge,
  type BreadcrumbItem,
  Breadcrumbs,
  DataGrid,
  EmptyState,
  Freshness,
  PageHeader,
  Skeleton,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { FilterRow } from "@/components/board";
import {
  type FoFailedDependency,
  type FoRequestPerformance,
  type FoResultCode,
  useFoRequests,
} from "@/lib/api";
import { useDrillParams } from "@/lib/drill";

import { FoAccessPanel, FoErrorsAlert, isPanelOk } from "./access-panel";
import { PanelCard } from "./panel-card";
import { DYNAMICS_PATH, RESULT_CODE_PARAM } from "./params";
import {
  dependencyLabel,
  formatCount,
  formatMs,
  formatPercent,
  formatWindow,
  resultCodeStatus,
  suppressionCaption,
} from "./status";

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Dynamics",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to={DYNAMICS_PATH} className={className}>
        {children}
      </Link>
    ),
  },
  { label: "API gateway", current: true },
];

export function DynamicsRequestsPage() {
  const requests = useFoRequests();
  const drill = useDrillParams(RESULT_CODE_PARAM);
  const data = requests.data;

  const selectedCodes = drill.getList(RESULT_CODE_PARAM);
  const codeRows = (data?.result_codes ?? []).filter(
    (row) => selectedCodes.length === 0 || selectedCodes.includes(row.result_code),
  );

  const labelledRequests = (data?.worst_requests ?? []).filter((row) => !row.is_other);

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="API gateway"
        subtitle="Every result code, failing outbound call and ranked operation from API Management, the gateway in front of F&O — the full lists behind the board's charts. The gateway's traffic, not F&O's own."
        actions={
          data?.collected_at ? (
            <Freshness
              label="Collected"
              since={data.collected_at}
              staleAfter={data.cache_ttl_sec * 2000}
            />
          ) : undefined
        }
      />

      {requests.error && (
        <Alert tone="danger" title="Couldn't load the API gateway page">
          {requests.error instanceof Error
            ? requests.error.message
            : "The NOVA API did not respond. This is a NOVA problem, not an Azure one."}
        </Alert>
      )}

      {requests.isPending && !data ? (
        <Skeleton height="24rem" />
      ) : (
        data &&
        (!isPanelOk(data) ? (
          <FoAccessPanel panel="API gateway requests" state={data} />
        ) : (
          <>
            {data.errors.length > 0 && <FoErrorsAlert errors={data.errors} />}

            <Typography variant="body-sm" tone="muted">
              Everything on this page covers the {formatWindow(data.window)}, read from the{" "}
              {data.workspace} workspace. No filter selects API Management: the queries ask for
              every request row the workspace holds, and as of 22 August 2026 the gateway is the
              only application reporting any. So read these as the gateway&apos;s calls into F&amp;O
              — the rankings are also capped upstream, which makes them worst-of lists rather than a
              census.
            </Typography>

            {/* One filter row, scoping the result-code grid it sits above (§4.3).
                It carries the donut's drill param, so arriving from the board
                lands pre-filtered. */}
            <FilterRow
              showRange={false}
              dimensions={[
                {
                  param: RESULT_CODE_PARAM,
                  label: "Result code",
                  noun: "code",
                  options: data.result_codes.map((row) => ({
                    value: row.result_code,
                    label: resultCodeStatus(row.result_code).label,
                  })),
                },
              ]}
            />

            <PanelCard
              title="Result codes the gateway returned"
              caption={`${formatCount(codeRows.length)} of ${formatCount(
                data.result_codes.length,
              )} shown`}
              padding={codeRows.length === 0 ? "md" : "none"}
            >
              {codeRows.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No result code matches this filter"
                  description="Clear the filter to see every code the gateway reported."
                />
              ) : (
                <DataGrid<FoResultCode>
                  data={codeRows}
                  getRowId={(row) => row.result_code}
                  density="compact"
                  aria-label="Request volume and failures per result code"
                  columns={[
                    {
                      id: "code",
                      header: "Result code",
                      accessor: (row) => row.result_code,
                      sortable: true,
                      cell: (row) => (
                        <Badge
                          tone={resultCodeStatus(row.result_code).tone}
                          variant="subtle"
                          size="xs"
                        >
                          {resultCodeStatus(row.result_code).label}
                        </Badge>
                      ),
                    },
                    {
                      id: "requests",
                      header: "Requests",
                      align: "end",
                      accessor: (row) => row.requests,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatCount(row.requests)}</span>
                      ),
                    },
                    {
                      id: "failed",
                      header: "Failed",
                      align: "end",
                      accessor: (row) => row.failed,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatCount(row.failed)}</span>
                      ),
                    },
                    {
                      id: "share",
                      header: "Share of window",
                      align: "end",
                      accessor: (row) => row.share_pct,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatPercent(row.share_pct)}</span>
                      ),
                    },
                    {
                      id: "p95",
                      header: "P95",
                      align: "end",
                      accessor: (row) => row.p95_duration_ms,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatMs(row.p95_duration_ms)}</span>
                      ),
                    },
                  ]}
                />
              )}
            </PanelCard>

            <PanelCard
              title="Failing outbound calls"
              caption={`${formatCount(
                data.failed_dependencies.length,
              )} listed · the calls these applications made onward, identified by type and target only, never by name`}
              padding={data.failed_dependencies.length === 0 ? "md" : "none"}
            >
              {data.failed_dependencies.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No outbound call reported a failure"
                  description="The dependency query returned no failing rows for this window."
                />
              ) : (
                <DataGrid<FoFailedDependency>
                  data={data.failed_dependencies}
                  getRowId={(row) => dependencyLabel(row.dependency_type, row.target)}
                  density="compact"
                  aria-label="Every failing outbound call by dependency type and target"
                  columns={[
                    {
                      id: "type",
                      header: "Type",
                      accessor: (row) => row.dependency_type,
                      sortable: true,
                    },
                    {
                      id: "target",
                      header: "Target",
                      accessor: (row) => row.target,
                      sortable: true,
                    },
                    {
                      id: "calls",
                      header: "Calls",
                      align: "end",
                      accessor: (row) => row.calls,
                      sortable: true,
                      cell: (row) => <span className="tabular-nums">{formatCount(row.calls)}</span>,
                    },
                    {
                      id: "failures",
                      header: "Failures",
                      align: "end",
                      accessor: (row) => row.failures,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatCount(row.failures)}</span>
                      ),
                    },
                    {
                      id: "rate",
                      header: "Failure rate",
                      align: "end",
                      accessor: (row) => row.failure_pct,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatPercent(row.failure_pct)}</span>
                      ),
                    },
                    {
                      id: "p95",
                      header: "P95",
                      align: "end",
                      accessor: (row) => row.p95_duration_ms,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatMs(row.p95_duration_ms)}</span>
                      ),
                    },
                  ]}
                />
              )}
            </PanelCard>

            <PanelCard
              title="Slowest gateway operations"
              caption={`${formatCount(labelledRequests.length)} labelled operation names ranked`}
              padding={data.worst_requests.length === 0 ? "md" : "none"}
            >
              {data.worst_requests.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No operation was ranked"
                  description="The workspace answered and held no request telemetry for this window."
                />
              ) : (
                <DataGrid<FoRequestPerformance>
                  data={data.worst_requests}
                  getRowId={(row) => row.request_name}
                  density="compact"
                  aria-label="Every ranked API Management operation, with its executions, failures and percentiles"
                  columns={[
                    {
                      id: "request",
                      header: "Operation",
                      accessor: (row) => row.request_name,
                      sortable: true,
                      cell: (row) =>
                        row.is_other ? (
                          <span className="text-fg-muted inline-flex items-center gap-2 text-sm italic">
                            Other
                            <Badge tone="neutral" variant="outline" size="xs">
                              folded cohort
                            </Badge>
                          </span>
                        ) : (
                          <span className="text-fg text-sm font-medium">{row.request_name}</span>
                        ),
                    },
                    {
                      id: "requests",
                      header: "Executions",
                      align: "end",
                      accessor: (row) => row.requests,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatCount(row.requests)}</span>
                      ),
                    },
                    {
                      id: "failed",
                      header: "Failed",
                      align: "end",
                      accessor: (row) => row.failed,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatCount(row.failed)}</span>
                      ),
                    },
                    {
                      id: "users",
                      header: "Distinct users",
                      align: "end",
                      accessor: (row) => row.distinct_users,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatCount(row.distinct_users)}</span>
                      ),
                    },
                    {
                      id: "avg",
                      header: "Mean",
                      align: "end",
                      accessor: (row) => row.avg_duration_ms,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatMs(row.avg_duration_ms)}</span>
                      ),
                    },
                    {
                      id: "p95",
                      header: "P95",
                      align: "end",
                      accessor: (row) => row.p95_duration_ms,
                      sortable: true,
                      cell: (row) => (
                        <span className="tabular-nums">{formatMs(row.p95_duration_ms)}</span>
                      ),
                    },
                    // No `Mean app` / `Mean SQL` / `SQL calls` columns: the
                    // workspace holds no SQL dependency rows, so the join behind
                    // them never matches. `avg_sql_ms` and `sql_calls` are
                    // coalesced zeros and `avg_app_ms` repeats `avg_duration_ms`
                    // — a decomposition of request time that was never measured.
                  ]}
                />
              )}
            </PanelCard>

            <Typography variant="body-sm" tone="muted">
              {suppressionCaption(data.suppression, "requests")}
            </Typography>
          </>
        ))
      )}
    </main>
  );
}
